import { useState, useEffect } from 'react';
import { Modal, Input, Button, Form, message, Space } from 'antd';
import { CopyOutlined, LinkOutlined } from '@ant-design/icons';
import { FronteggContext } from '@frontegg/rest-api';

import { ZoomMtg } from '@zoom/meetingsdk';
import { InfoCircleIcon } from 'src/icons';
import participantZoom from 'src/assets/participantZoom.png';
import copyInvitation from 'src/assets/copyInvitation.png';
import { AxiosAmbient } from 'src/lib';

const ZoomMeetingModal = ({
  showZoomModal,
  setShowZoomModal,
  setupJoinButtonAutomation,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [joining, setJoining] = useState(false);
  const [isInstructionsOpen, setIsInstructionsOpen] = useState(false);

  // Function to get fresh access token
  const getFreshAccessToken = async () => {
    try {
      const refreshToken = localStorage.getItem('zoomRefreshToken');

      if (!refreshToken) {
        throw new Error('No refresh token found in localStorage');
      }

      console.log('🔄 Getting fresh access token...');

      const headers = {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        Authorization: `Bearer ${FronteggContext.getAccessToken()}`,
      };

      const requestBody = {
        refresh_token: refreshToken,
      };

      const result = await AxiosAmbient.post(
        '/zoom-refresh-access-token',
        requestBody,
        {
          headers,
        }
      );

      const response = result?.data;
      console.log('refresh token response', response);

      if (response?.access_token) {
        const { access_token, refresh_token: new_refresh_token } = response;

        // Update localStorage with new tokens
        localStorage.setItem('zoomAccessToken', access_token);
        if (new_refresh_token && new_refresh_token !== refreshToken) {
          localStorage.setItem('zoomRefreshToken', new_refresh_token);
          console.log('🔄 Updated refresh token in localStorage');
        }

        console.log('✅ Fresh access token obtained and stored');
        return access_token;
      } else {
        throw new Error(
          'Failed to refresh access token - no access_token in response'
        );
      }
    } catch (error) {
      console.error('❌ Error getting fresh access token:', error);

      // If refresh fails, clear tokens and ask user to re-authenticate
      localStorage.removeItem('zoomAccessToken');
      localStorage.removeItem('zoomRefreshToken');

      message.error(
        'Authentication expired. Please re-authenticate with Zoom.'
      );
      throw error;
    }
  };

  // Handle form submission
  const handleSubmit = async () => {
    try {
      // Validate form
      const values = await form.validateFields();

      // Extract meeting details
      const meetingLink = values.meetingLink;
      if (!meetingLink) {
        message.error('Please enter a Zoom meeting link');
        return;
      }

      const meetingDetails = extractMeetingDetails(meetingLink);

      if (!meetingDetails.meetingNumber) {
        message.error('Invalid Zoom meeting link format');
        return;
      }

      // Check if we have refresh token in localStorage
      const refreshToken = localStorage.getItem('zoomRefreshToken');
      if (!refreshToken) {
        message.error(
          'No Zoom authentication found. Please authenticate first.'
        );
        return;
      }

      // Start joining process
      setLoading(true);
      message.loading({
        content: 'Preparing to join meeting...',
        key: 'joinMsg',
      });
      console.log('Meeting details:', meetingDetails);

      // Get signature from backend
      const headers = {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        Authorization: `Bearer ${FronteggContext.getAccessToken()}`,
      };

      const requestBody = {
        meetingNumber: meetingDetails.meetingNumber,
        role: 0, // 1 for host
      };

      const result = await AxiosAmbient.post('/zoom-signature', requestBody, {
        headers,
      });

      const response = result?.data;
      console.log('signature response', response);

      if (!response?.signature) {
        throw new Error('Failed to generate meeting signature');
      }

      console.log('values123', values);
      // Prepare to join meeting
      setJoining(true);

      // Join the meeting
      await joinZoomMeeting({
        meetingNumber: meetingDetails.meetingNumber,
        signature: response.signature,
        sdkKey: response.sdkKey,
        userName: 'AURA AI Scribe',
        password: meetingDetails.password,
      });
    } catch (error) {
      console.error('Error joining meeting:', error);
      message.error({
        content:
          'Failed to join meeting: ' + (error.message || 'Unknown error'),
        key: 'joinMsg',
      });
      setLoading(false);
      setJoining(false);
    }
  };

  // Function to start cloud recording
  const startCloudRecording = async (meetingId) => {
    try {
      console.log(
        '🎥 Attempting to start cloud recording for meeting:',
        meetingId
      );

      // Step 1: Get fresh access token
      const accessToken = await getFreshAccessToken();

      // Step 2: Start recording with the access token
      message.loading({
        content: 'Starting cloud recording...',
        key: 'recordingMsg',
      });

      const headers = {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        Authorization: `Bearer ${FronteggContext.getAccessToken()}`,
      };

      const requestBody = {
        meeting_id: meetingId,
        access_token: accessToken,
      };

      const result = await AxiosAmbient.post(
        '/zoom-start-recording',
        requestBody,
        {
          headers,
        }
      );

      const response = result?.data;
      console.log('recording response', response);

      if (response?.success) {
        console.log('✅ Cloud recording started successfully');
        message.success({
          content: 'Cloud recording started successfully!',
          key: 'recordingMsg',
          duration: 5,
        });
      } else {
        throw new Error(response?.error || 'Failed to start recording');
      }
    } catch (error) {
      console.error('❌ Error starting cloud recording:', error);
    }
  };

  // Function to check if user is authenticated
  const checkZoomAuth = () => {
    const accessToken = localStorage.getItem('zoomAccessToken');
    const refreshToken = localStorage.getItem('zoomRefreshToken');

    return {
      isAuthenticated: !!(accessToken || refreshToken),
      hasRefreshToken: !!refreshToken,
      hasAccessToken: !!accessToken,
    };
  };

  // Extract meeting details from link
  const extractMeetingDetails = (link) => {
    try {
      // Meeting ID pattern
      const meetingIdMatch = link.match(/\/j\/(\d+)/);
      // Password pattern - handles both pwd= and pwd%3D formats
      const passwordMatch =
        link.match(/[?&]pwd=([^&]+)/) || link.match(/[?&]pwd%3D([^&]+)/);

      return {
        meetingNumber: meetingIdMatch ? meetingIdMatch[1] : null,
        password: passwordMatch ? decodeURIComponent(passwordMatch[1]) : null,
      };
    } catch (error) {
      console.error('Error extracting meeting details:', error);
      return { meetingNumber: null, password: null };
    }
  };

  // Join Zoom meeting
  const joinZoomMeeting = (params) => {
    console.log('Joining meeting with params:', {
      meetingNumber: params.meetingNumber,
      sdkKey: params.sdkKey,
      userName: params.userName,
      password: params.password ? '******' : 'none',
    });

    return new Promise((resolve, reject) => {
      ZoomMtg.init({
        leaveUrl: import.meta.env.VITE_AMBIENT_UI_BASE_URL,
        isSupportAV: false, // Disable Audio/Video completely
        disableJoinAudio: true,
        success: (success) => {
          console.log('Zoom SDK initialized successfully');
          console.log('params', params);

          ZoomMtg.join({
            sdkKey: params.sdkKey,
            signature: params.signature,
            meetingNumber: params.meetingNumber,
            passWord: params.password,
            userName: 'Aura AI Scribe',
            success: (success) => {
              console.log('Join command successful', success);
              resolve(success);
              setLoading(false);
            },
            error: (error) => {
              console.error('Error joining meeting:', error);
              reject(error);
              setLoading(false);
              setJoining(false);
              message.error({
                content:
                  'Failed to join meeting: ' +
                  (error.errorMessage || JSON.stringify(error)),
                key: 'joinMsg',
              });
            },
          });
          setTimeout(() => {
            setupJoinButtonAutomation();

            // Start recording after a delay to ensure we're fully joined
            setTimeout(async () => {
              await startCloudRecording(params.meetingNumber);
            }, 5000); // Wait 10 seconds after joining to start recording
          }, 3000);

          setShowZoomModal(false);
        },
        error: (error) => {
          console.error('Error initializing Zoom:', error);
          reject(error);
          setLoading(false);
          setJoining(false);
          message.error({
            content:
              'Failed to initialize Zoom: ' +
              (error.errorMessage || JSON.stringify(error)),
            key: 'joinMsg',
          });
        },
      });
    });
  };

  // Handle cancel/close
  const handleCancel = () => {
    if (!loading && !joining) {
      form.resetFields();
      setShowZoomModal(false);
    }
  };

  // Check authentication status on component mount
  useEffect(() => {
    const authStatus = checkZoomAuth();
    console.log('Zoom authentication status:', authStatus);
  }, []);

  return (
    <Modal
      title="Add to live meeting"
      open={showZoomModal}
      onCancel={handleCancel}
      footer={null}
      width={600}
      maskClosable={!loading}
      closable={!loading}
      destroyOnClose={true}
      className="zoom-meeting-modal"
    >
      <Form form={form} layout="vertical">
        <Form.Item label="Name your encounter (Optional)" name="meetingName">
          <Input placeholder="Patient: John Doe" disabled={loading} />
        </Form.Item>

        <Form.Item
          label="Meeting link"
          name="meetingLink"
          rules={[
            { required: true, message: 'Please enter a meeting link' },
            {
              pattern: /zoom\.us\/j\/\d+/,
              message: 'Please enter a valid Zoom meeting link',
            },
          ]}
          // extra="Capture meetings from Zoom, MS teams, and more."
        >
          <Input
            prefix={<LinkOutlined />}
            placeholder="https://zoom.us/j/1234567890"
            disabled={loading}
          />
        </Form.Item>
        <div className=" w-full my-3">
          <button
            className="underline text-[#001bde] flex justify-between items-center w-full p-0 "
            onClick={() => setIsInstructionsOpen(!isInstructionsOpen)}
          >
            <div className=" flex items-center gap-1 text-sm">
              <InfoCircleIcon size={14} className="size-4" />
              How to enable audio transcription
            </div>
          </button>

          {isInstructionsOpen && (
            <div className="mt-2 p-4 bg-green-50 rounded-lg">
              <h3 className="font-medium text-sm text-gray-800 mb-3">
                How to Copy Zoom Meeting Link:
              </h3>

              <div className="flex mb-4">
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-green-200 flex items-center justify-center mr-3">
                  <span className="text-[#00d091] text-sm">1</span>
                </div>
                <div>
                  <p className="text-gray-800 font-medium text-sm">
                    Open your Zoom meeting or scheduled meeting
                  </p>
                  <p className="text-[#000000E0] flex items-start text-xs bg-white p-2 rounded-md">
                    <LinkOutlined size={12} className="mr-2 mt-1" />
                    Start or join your Zoom meeting first
                  </p>
                </div>
              </div>

              <div className="flex mb-4">
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-green-200 flex items-center justify-center mr-3">
                  <span className="text-[#00d091] text-sm">2</span>
                </div>
                <div>
                  <p className="text-gray-800 font-medium text-sm">
                    Click on "Participants" or "Invite" button
                  </p>
                  <p className="text-[#000000E0] flex items-start text-xs bg-white p-2 rounded-md">
                    <span className="mr-2">👥</span>
                    Look for the invite option in your Zoom controls
                  </p>
                  <div
                    className="w-full overflow-hidden rounded border border-gray-200"
                    style={{ maxHeight: '120px' }}
                  >
                    <img
                      src={participantZoom}
                      alt="Participant"
                      className="w-full object-cover object-top"
                      style={{ maxHeight: '120px' }}
                    />
                  </div>
                </div>
              </div>

              <div className="flex">
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-green-200 flex items-center justify-center mr-3">
                  <span className="text-[#00d091]">3</span>
                </div>
                <div>
                  <p className="text-gray-800 font-medium text-sm">
                    Copy the meeting link and paste it here
                  </p>
                  <p className="text-[#000000E0] flex items-start text-xs bg-white p-2 rounded-md">
                    <CopyOutlined size={14} className="mr-2 mt-[2px]" />
                    AURA AI bot will join to start cloud recording
                  </p>
                  <div
                    className="w-full overflow-hidden rounded border border-gray-200"
                    style={{ maxHeight: '120px' }}
                  >
                    <img
                      src={copyInvitation}
                      alt="InviteClicked"
                      className="w-full object-cover object-top"
                      style={{ maxHeight: '120px' }}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <Form.Item className="modal-footer">
          <Space style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button onClick={handleCancel} disabled={loading}>
              Cancel
            </Button>
            <Button
              type="primary"
              onClick={handleSubmit}
              loading={loading}
              disabled={joining}
            >
              {joining ? 'Joining...' : 'Start Capturing'}
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default ZoomMeetingModal;
